// When enemies arrive: the spawn rate fills a budget, and every WAVE_PERIOD seconds the budget walks in as one wave.
// A run keeps the enemy count (and so the xp, gold and kill limits) of a steady stream. Pure: node tests load it directly.
export const WAVE_PERIOD = 15;
export const WAVE_RELEASE = 5; // seconds one wave takes to arrive; the rest of the period is the calm

const FIRST_WAVE_AT = 1;
const FIRST_WAVE_SIZE = 6; // the opening seconds would otherwise offer almost nothing to shoot
const FIELD_CLEAR = 2; // this few enemies left and the next wave comes early...
const EARLY_SHARE = 0.5; // ...once it would be at least this share of a full wave
const TWO_FRONTS_FROM = 9;
const FRONT_WIDTH = 14; // enemies side by side on a front
const FRONT_SPACING = 2; // world units between them, wider than the largest common enemy
const TURN = 2.4; // radians between wave directions: never the same side twice running

export class WaveClock {
  wave = 0;
  private budget = FIRST_WAVE_SIZE;
  private nextAt = FIRST_WAVE_AT;
  private left = 0;
  private perSecond = 0;
  private acc = 0;
  private angle = 0;
  private slot = 0;

  reset(): void {
    this.wave = 0;
    this.budget = FIRST_WAVE_SIZE;
    this.nextAt = FIRST_WAVE_AT;
    this.left = 0;
    this.acc = 0;
  }

  // Advances the clock and returns how many enemies arrive this frame. `rate` is enemies per second right now.
  step(dt: number, t: number, rate: number, hostile: number, rng: () => number): number {
    this.budget += rate * dt;
    const earlyWave = t >= FIRST_WAVE_AT && hostile <= FIELD_CLEAR && this.budget >= rate * WAVE_PERIOD * EARLY_SHARE;
    if (this.left <= 0 && (t >= this.nextAt || earlyWave)) this.begin(t, rng);
    if (this.left <= 0) return 0;
    this.acc += this.perSecond * dt;
    const n = Math.min(this.left, Math.floor(this.acc));
    this.acc -= n;
    this.left -= n;
    return n;
  }

  // The direction, seen from the player, of the next enemy to arrive at this distance.
  nextAngle(radius: number): number {
    const i = this.slot++;
    const twoFronts = this.wave >= TWO_FRONTS_FROM;
    const place = twoFronts ? Math.floor(i / 2) : i;
    const column = (place % FRONT_WIDTH) - (FRONT_WIDTH - 1) / 2;
    return this.angle + (twoFronts && i % 2 === 1 ? Math.PI : 0) + (column * FRONT_SPACING) / radius;
  }

  private begin(t: number, rng: () => number): void {
    this.nextAt = t + WAVE_PERIOD;
    const size = Math.floor(this.budget);
    if (size <= 0) return;
    this.budget -= size;
    this.left = size;
    this.perSecond = size / WAVE_RELEASE;
    this.acc = 1; // the first enemy arrives as the wave begins
    this.angle = this.wave === 0 ? rng() * Math.PI * 2 : this.angle + TURN + (rng() - 0.5) * 0.6;
    this.slot = 0;
    this.wave += 1;
  }
}
